/**
 * TruePrice V1 - Fidelity Tier Types
 * 
 * Defines the 5 fidelity tiers (L1-L5) and their properties.
 * Source: SSOT Core Formulas, TruePrice_Tier_Lookup_Tables.md, Addendum E
 */

export const TIER_KEYS = ['L1', 'L2', 'L3', 'L4', 'L5'] as const;

export type TierKey = typeof TIER_KEYS[number];

export interface TierConfig {
  key: TierKey;
  label: string;
  shortLabel: string;
  multiplier: number;      // F - Fidelity multiplier for labor
  scopeTax: number;        // T - Risk buffer percentage
  densityMin: number;      // Minimum h/sq in (before relaxer)
  densityMax: number;      // Maximum h/sq in (before relaxer)
  description: string;
  examples: string[];
}

export const TIERS: Record<TierKey, TierConfig> = {
  L1: {
    key: 'L1',
    label: 'Study',
    shortLabel: 'Study',
    multiplier: 1.00,
    scopeTax: 0.02,
    densityMin: 0.004,
    densityMax: 0.008,
    description: 'Quick practice piece. Loose, fast, no pressure.',
    examples: ['Daily practice paintings', 'Gestural studies', 'Plein air quick captures', 'Value studies'],
  },
  L2: {
    key: 'L2',
    label: 'Studio',
    shortLabel: 'Studio',
    multiplier: 1.10,
    scopeTax: 0.05,
    densityMin: 0.009,
    densityMax: 0.013,
    description: 'Personal work made to keep or sell someday.',
    examples: ['Abstract expressionism', 'Color field paintings', 'Personal portfolio work'],
  },
  L3: {
    key: 'L3',
    label: 'Commission',
    shortLabel: 'Commission',
    multiplier: 1.20,
    scopeTax: 0.08,
    densityMin: 0.015,
    densityMax: 0.025,
    description: 'Someone paid you to make this specific piece.',
    examples: ['Portrait commissions', 'Pet portraits', 'Landscape commissions', 'Standard gallery work'],
  },
  L4: {
    key: 'L4',
    label: 'Exhibition',
    shortLabel: 'Exhibition',
    multiplier: 1.40,
    scopeTax: 0.12,
    densityMin: 0.030,
    densityMax: 0.050,
    description: 'Made for gallery or institutional show.',
    examples: ['Juried exhibitions', 'Gallery centerpieces', 'Complex multi-layer work'],
  },
  L5: {
    key: 'L5',
    label: 'Masterwork',
    shortLabel: 'Masterwork',
    multiplier: 1.75,
    scopeTax: 0.20,
    densityMin: 0.060,
    densityMax: 0.100, // Note: Can exceed this for L5 (Addendum E)
    description: 'For permanent collections. Not made for interior walls.',
    examples: ['Photorealistic portraits', 'Museum acquisitions', 'Career-defining pieces'],
  },
};

export function isTierKey(value: string): value is TierKey {
  return TIER_KEYS.includes(value as TierKey);
}

export function getTierConfig(key: TierKey): TierConfig {
  return TIERS[key];
}

/**
 * Find the appropriate tier based on density
 * Used for tier suggestions when density doesn't match selected tier
 */
export function findTierByDensity(density: number): TierKey {
  // Check from highest to lowest
  if (density >= 0.060) return 'L5';
  if (density >= 0.030) return 'L4';
  if (density >= 0.015) return 'L3';
  if (density >= 0.009) return 'L2';
  return 'L1';
}
